import { handleFreeReturn } from '@lambda/commons/helpers';
import Image from 'next/image';
import i18n from '@/settings/i18n';
import { generateWarrantyLabel } from './helpers';
import { useCollectionPageContext } from '@/components/collections-v3/hooks/useCollectionPageContext';
import settings from '@/settings';
import TrustPilot from './Trustpilot';
import { getEdgeUrl } from '@/lib/getEdgeUrl';
import CategoryFilters from '../main/filters/CategoryFilters';
import Spotlight from './spotlight';
import { CATEGORY_SLOT, resolveRefurbishedTitle } from '../refurbishedTitle';

const t = i18n.init('collections');
const tCategory = i18n.init('categories');

const Banner = () => {
  const {
    collectionName,
    categoryName,
    categoryWarrantyInfo,
    type,
    spotlightedProducts,
  } = useCollectionPageContext();

  if (type === 'MS_CATALOG_DEALS') {
    return (
      <div className="xl:reebelo-container mx-auto pb-5 sm:p-5 xl:px-8">
        <Spotlight skus={spotlightedProducts} />
        <div className="mb-4 mt-5 px-5 md:mt-0 md:px-0">
          <h1 className="text-xl font-bold">{t`Hot Deals`}</h1>
        </div>
        <CategoryFilters className="px-5 sm:px-0" />
      </div>
    );
  }

  const warrantyLabel = generateWarrantyLabel(
    categoryName,
    categoryWarrantyInfo,
  );
  const freeReturn = t`${{
    days: handleFreeReturn(settings.store, settings.free_return_days),
  }} Days Return`;
  const customers = t`500,000+ customers`;

  // Hero title = localized category name + locale-specific "certified
  // refurbished" wording, agreeing in gender/number. Word order and wording
  // live in the locale files, keyed on {{category}}:
  //   en → "Certified Refurbished {{category}}"
  //   fr → "{{category}} reconditionné(e)s certifié(e)s"
  // A generic per-locale template covers every category (French defaults to
  // masculine plural); categories whose agreement differs (e.g. feminine FR
  // nouns) add a `Certified Refurbished <name>` override to the locale file.
  const localizedCategory = tCategory(`${collectionName}`);
  const heroTitle = resolveRefurbishedTitle(
    t,
    'Certified Refurbished',
    collectionName,
    t`Certified Refurbished ${{ category: CATEGORY_SLOT }}`,
  );
  const [titleBeforeCategory, titleAfterCategory = ''] =
    heroTitle.split(CATEGORY_SLOT);

  return (
    <div className="xl:reebelo-container mx-auto pb-5 md:p-5 xl:px-8">
      <div className="relative overflow-hidden bg-gray-700 p-5 text-white md:rounded xl:p-8">
        <div className="w-full xs:w-[calc(100%-80px)] sm:w-[calc(100%-150px)] xl:w-[calc(100%-160px)]">
          <h1 className="text-lg font-bold lg:text-xl ">
            {titleBeforeCategory}
            <span className="text-teal-500">{localizedCategory}</span>
            {titleAfterCategory}
          </h1>
          <div className="mt-4 flex items-center justify-between gap-x-2">
            <div className="flex flex-wrap gap-x-1 text-xxs font-bold xxs:text-xs xs:gap-x-2 md:text-sm lg:gap-x-3 xl:gap-x-4 xl:text-base">
              <span>{t(warrantyLabel)}</span>
              <span>|</span>
              <span>{freeReturn}</span>
              <span>|</span>
              <div>
                <span className="mr-1 hidden md:inline-block">{customers}</span>
                <span className="inline-block md:hidden">
                  <TrustPilot />
                </span>
              </div>
            </div>
            <div className="hidden md:block">
              <TrustPilot />
            </div>
          </div>
        </div>
        <div className="absolute bottom-[-10px] right-[-50px] w-[130px] sm:bottom-0 sm:right-5 xl:w-[150px]">
          <Image
            src={getEdgeUrl('images/collections/banner/default/phones.png')}
            alt="phones"
            loading="eager"
            className="h-auto w-full rounded-r-md object-cover object-left opacity-20 xs:opacity-100 lg:rounded-none"
            width={292}
            height={206}
            priority
          />
        </div>
      </div>
    </div>
  );
};

export default Banner;
